import * as React from 'react';
import { ChevronRight, GripVertical, Pin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Tooltip } from '@/components/ui/tooltip';

const STORAGE_KEY = 'zoning-sidebar-width';
const COLLAPSED_KEY = 'zoning-sidebar-collapsed';

const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 220;
const MAX_WIDTH = 480;
const SNAP_COLLAPSE_AT = 190;
const COLLAPSED_WIDTH = 28;
const HOVER_OPEN_DELAY = 220;
const HOVER_CLOSE_DELAY = 160;

function readWidth(): number {
  if (typeof window === 'undefined') return DEFAULT_WIDTH;
  const v = Number(window.localStorage.getItem(STORAGE_KEY));
  if (!Number.isFinite(v) || v <= 0) return DEFAULT_WIDTH;
  return Math.min(Math.max(v, MIN_WIDTH), MAX_WIDTH);
}

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(COLLAPSED_KEY) === '1';
}

interface ResizableSidebarProps {
  children: React.ReactNode;
  className?: string;
}

/** Right-side sidebar.
 *
 *  Layout pattern: a flow-sized placeholder sets how much horizontal space
 *  the sidebar reserves; the actual panel is positioned absolutely against
 *  the placeholder (always — never toggled between absolute/relative). This
 *  means the canvas only reflows on permanent collapse/expand, never on
 *  hover-overlay open/close — eliminating the "image jumps" glitch.
 *
 *  - Permanent open: placeholder = width; panel = width (overlays the
 *    placeholder exactly, looks inline).
 *  - Collapsed: placeholder = COLLAPSED_WIDTH; panel = COLLAPSED_WIDTH.
 *  - Hover overlay: placeholder = COLLAPSED_WIDTH; panel = width, layered
 *    over the canvas with z-30 + shadow. Canvas doesn't reflow.
 *
 *  Hover open/close has delays so the panel isn't jumpy on cursor flickers.
 *  Drag-resize past the snap threshold previews the next state by dimming
 *  the panel and lighting up the drag rule.
 *
 *  No "pin" button. Two states: collapsed (top half is a big hover/click
 *  zone, bottom half shows a drag-grip glyph) and open (collapse-button
 *  bottom-right). Hover overlay shows no buttons. */
export function ResizableSidebar({ children, className }: ResizableSidebarProps) {
  const { t } = useT();
  const isNarrow = useMediaQuery('(max-width: 768px)');

  const [width, setWidth] = React.useState<number>(readWidth);
  const [collapsed, setCollapsed] = React.useState<boolean>(() =>
    readCollapsed() || (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches)
  );
  const [hoverExpanded, setHoverExpanded] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const [snapPreview, setSnapPreview] = React.useState(false);

  React.useEffect(() => { if (isNarrow) setCollapsed(true); }, [isNarrow]);
  React.useEffect(() => { try { window.localStorage.setItem(STORAGE_KEY, String(width)); } catch { /* ignore */ } }, [width]);
  React.useEffect(() => {
    try {
      if (collapsed) window.localStorage.setItem(COLLAPSED_KEY, '1');
      else window.localStorage.removeItem(COLLAPSED_KEY);
    } catch { /* ignore */ }
  }, [collapsed]);

  // ── Hover open/close with delay ────────────────────────────────────────
  const openTimerRef = React.useRef<number | null>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const cancelTimers = () => {
    if (openTimerRef.current != null) { window.clearTimeout(openTimerRef.current); openTimerRef.current = null; }
    if (closeTimerRef.current != null) { window.clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  };
  const requestOpen = () => {
    if (openTimerRef.current != null) return;
    cancelTimers();
    openTimerRef.current = window.setTimeout(() => { setHoverExpanded(true); openTimerRef.current = null; }, HOVER_OPEN_DELAY);
  };
  const requestClose = () => {
    if (closeTimerRef.current != null) return;
    cancelTimers();
    closeTimerRef.current = window.setTimeout(() => { setHoverExpanded(false); closeTimerRef.current = null; }, HOVER_CLOSE_DELAY);
  };
  React.useEffect(() => () => cancelTimers(), []);

  // ── Drag-to-resize ─────────────────────────────────────────────────────
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(width);
  const wasCollapsedRef = React.useRef(collapsed);

  const onPointerDownHandle = (e: React.PointerEvent) => {
    if (isNarrow) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    startXRef.current = e.clientX;
    startWidthRef.current = collapsed ? COLLAPSED_WIDTH : width;
    wasCollapsedRef.current = collapsed;
    setDragging(true);
    setSnapPreview(false);
    cancelTimers();
    setHoverExpanded(false);
  };

  const onPointerMoveHandle = (e: React.PointerEvent) => {
    if (!dragging) return;
    const dx = startXRef.current - e.clientX;
    const next = startWidthRef.current + dx;
    if (wasCollapsedRef.current) {
      setSnapPreview(next >= SNAP_COLLAPSE_AT);
    } else {
      setSnapPreview(next < SNAP_COLLAPSE_AT);
    }
    // Update width whenever the drag position is in the expanded range —
    // makes the preview-expanded state size to the cursor. Below threshold,
    // don't touch width (the saved expanded-width stays clean for snap-back).
    if (next >= SNAP_COLLAPSE_AT) {
      setWidth(Math.min(Math.max(next, MIN_WIDTH), MAX_WIDTH));
    }
  };

  const onPointerUpHandle = (e: React.PointerEvent) => {
    if (!dragging) return;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    setDragging(false);
    setSnapPreview(false);
    const dx = startXRef.current - e.clientX;
    const next = startWidthRef.current + dx;
    if (wasCollapsedRef.current) {
      if (next >= SNAP_COLLAPSE_AT) setCollapsed(false);
    } else {
      if (next < SNAP_COLLAPSE_AT) setCollapsed(true);
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────
  // While dragging past the snap threshold, render the *future* state so the
  // user can see what'll happen on release. effectiveCollapsed = wasCollapsed
  // XOR snapPreview (snap flips state).
  const effectiveCollapsed = dragging
    ? (wasCollapsedRef.current !== snapPreview)
    : collapsed;

  const overlayMode = !dragging && collapsed && hoverExpanded;
  const flowWidth = effectiveCollapsed ? COLLAPSED_WIDTH : width;
  const panelWidth = (effectiveCollapsed && !overlayMode) ? COLLAPSED_WIDTH : width;
  const showFullBody = !effectiveCollapsed || overlayMode;
  const dragOpacity = dragging && snapPreview ? 0.55 : 1;

  // While dragging, suppress the width transition (panel must follow cursor).
  const widthTransition = dragging ? 'none' : 'width 180ms ease, opacity 120ms ease';

  // Outside-tap dismiss when overlay is showing — primarily for touch /
  // narrow devices where mouseLeave isn't reliable. Listens at document
  // level; checks if the tap landed outside the panel.
  const panelRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!overlayMode) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      // Ignore taps that land inside a portaled overlay (long-press action
      // sheet, delete-confirm dialog). Those portal to document.body — outside
      // panelRef — so without this guard the first pointerdown on a sheet
      // button would collapse the overlay and unmount the sidebar (along with
      // the sheet's state) before the action could run.
      if (target?.closest?.('[data-overlay]')) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        cancelTimers();
        setHoverExpanded(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [overlayMode]);

  return (
    <div
      className={cn('relative shrink-0 self-stretch', className)}
      style={{ width: flowWidth, transition: dragging ? 'none' : 'width 180ms ease' }}
    >
      {/* Panel — always absolute against the placeholder, never toggled
          between absolute/relative. Width animates between collapsed strip
          and full panel; canvas only reflows when `flowWidth` changes
          (i.e. on permanent collapse/expand, not on hover overlay). */}
      <div
        ref={panelRef}
        className={cn(
          'absolute right-0 top-0 bottom-0 flex flex-col border-l border-border bg-card text-card-foreground',
          overlayMode && 'z-30 shadow-xl',
        )}
        style={{ width: panelWidth, transition: widthTransition, opacity: dragOpacity }}
        onMouseEnter={() => { if (overlayMode) cancelTimers(); }}
        onMouseLeave={() => { if (overlayMode) requestClose(); }}
      >
        {/* Drag handle — full vertical strip on the left edge. */}
        {!isNarrow && (
          <div
            className={cn(
              'absolute left-0 top-0 bottom-0 z-40 w-2 -translate-x-1/2 cursor-col-resize',
              'before:absolute before:left-1/2 before:top-0 before:bottom-0 before:w-px before:-translate-x-1/2',
              'before:transition-colors before:duration-100',
              snapPreview
                ? 'before:bg-primary'
                : dragging
                  ? 'before:bg-primary/70'
                  : 'before:bg-transparent hover:before:bg-primary/40',
            )}
            onPointerDown={onPointerDownHandle}
            onPointerMove={onPointerMoveHandle}
            onPointerUp={onPointerUpHandle}
            onPointerCancel={onPointerUpHandle}
            role="separator"
            aria-orientation="vertical"
            aria-label={t('zoning.sidebar.resize')}
          />
        )}

        {showFullBody ? (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto thin-scrollbar pb-10">
              {children}
            </div>
            {/* Bottom-right collapse button — only when permanently open. */}
            {!isNarrow && !overlayMode && (
              <Tooltip content={t('zoning.sidebar.collapse')} side="left">
                <button
                  type="button"
                  onClick={() => { setCollapsed(true); setHoverExpanded(false); }}
                  aria-label={t('zoning.sidebar.collapse')}
                  className={cn(
                    'absolute bottom-2 right-2 z-30 grid h-6 w-6 place-items-center rounded-md',
                    'border border-border bg-card/90 text-muted-foreground backdrop-blur-sm transition-colors',
                    'hover:bg-muted hover:text-foreground',
                  )}
                >
                  <ChevronRight className="size-3.5" />
                </button>
              </Tooltip>
            )}
            {/* Bottom-right pin button — only during hover overlay (commits
                to permanent open). Wide screens only. */}
            {!isNarrow && overlayMode && (
              <Tooltip content={t('zoning.sidebar.pin')} side="left">
                <button
                  type="button"
                  onClick={() => { setCollapsed(false); setHoverExpanded(false); cancelTimers(); }}
                  aria-label={t('zoning.sidebar.pin')}
                  className={cn(
                    'absolute bottom-2 right-2 z-30 grid h-6 w-6 place-items-center rounded-md',
                    'border border-border bg-card/90 text-muted-foreground backdrop-blur-sm transition-colors',
                    'hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Pin className="size-3.5" />
                </button>
              </Tooltip>
            )}
          </>
        ) : (
          /* Collapsed strip:
             - Entire strip body is the slide-out trigger.
             - Desktop: hover (delayed) opens the overlay; mouseLeave closes.
             - Touch / narrow: tap toggles the overlay (most touch devices
               don't fire stable hover, so onClick is the canonical gesture).
             - Bottom-right corner has a small permanent-expand button on
               desktop only. Hidden on narrow — permanent open isn't useful
               when the panel would cover the whole screen. */
          <div
            className="relative flex flex-1 flex-col items-center justify-center text-muted-foreground/40"
            onMouseEnter={() => { if (!dragging) requestOpen(); }}
            onMouseLeave={() => requestClose()}
            onClick={() => {
              cancelTimers();
              setHoverExpanded(o => !o);
            }}
            role="button"
            tabIndex={0}
          >
            <GripVertical className="size-3.5" />
            {/* Permanent-expand affordance — full-width strip at the bottom
                of the collapsed column. Visually subtle (no border, no card
                fill, just a chevron + hover tint) so it doesn't compete with
                the panel content. Hovering it cancels the pending overlay
                so the user can click without seeing it slide out first.
                Wide screens only — on narrow, permanent open isn't useful. */}
            {!isNarrow && (
              <Tooltip content={t('zoning.sidebar.expand')} side="left">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setCollapsed(false); setHoverExpanded(false); }}
                  onMouseEnter={(e) => { e.stopPropagation(); cancelTimers(); }}
                  onMouseLeave={(e) => { e.stopPropagation(); if (!dragging) requestOpen(); }}
                  aria-label={t('zoning.sidebar.expand')}
                  className={cn(
                    'absolute bottom-0 left-0 right-0 flex h-7 items-center justify-center',
                    'text-muted-foreground/70 transition-colors',
                    'hover:bg-muted hover:text-foreground',
                  )}
                >
                  <ChevronRight className="size-3.5 rotate-180" />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
